import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHistoryState, useOpenRowInHistory } from '../hooks/useViewInHistory';
import LoadingSpinner from '../components/LoadingSpinner';
import { apiGet, apiThrow, API_BASE } from '../utils/api';
import ErrorMessage from '../components/ErrorMessage';
import { useDepartment } from '../contexts/DepartmentContext';
import useDeepLinkParam from '../hooks/useDeepLinkParam';
import Pagination, { getStoredPageSize } from '../components/Pagination';
// STATUS_COLORS moved with the status pills into `components/OrderFilterBar`.
// 🔻 `URGENCY_COLORS` came OFF this import 2026-08-18 — it was imported here
// but never actually read (a pre-existing dead import, found while retiring
// `UrgencyBadge.js`). `URGENCY_LABELS` and `URGENCY_PRIORITY` came off on
// 2026-08-09 with the `Sort: Urgency` reducer; all three are now DELETED from
// `constants/colors.js`/`statuses.js` (owner ruling) — nothing writes
// `orders.urgency` any more either. Do not re-add any of them.
import {
  // 🔀 THE QUEUE TABS (2026-09-12) REPLACED THIS PAGE'S STATUS MULTI-SELECT AND
  // ITS COMPLETED PILL. `ORDER_STATUSES`, `matchesCompletionFilter`,
  // `normalizeStoredShowCompleted` and `serializeShowCompleted` came off this
  // import with them. 🔻 2026-09-30: `MyOrders.js` swapped its checkboxes for
  // tabs too, so the three completion helpers now have NO production importer
  // — declared owed work at `constants/statuses.js`, not deleted here.
  ORDER_QUEUE_TABS,
  ORDER_QUEUE_TAB_IDS,
  orderQueueTab,
  orderQueueTabs,
  // 🔻 `STATUS_LABELS` came off this import 2026-10-28: its one reader, the
  // move toast's fallback word, moved to `hooks/useHeldOpenRows.js`.
} from '../constants/statuses';
import EmptyState from '../components/EmptyState';
// 🔻 `OrderPdfButton` WAS IMPORTED HERE AND IS GONE (2026-08-10). Its three
// actions are now rows inside the actions menu (`OrderPdfMenuItems`), per the
// owner's *"include an export excell and print options in the action menu"*.
// The component itself is untouched and still serves `MyOrders.js` and
// `OrderPage.js`; only this page stopped rendering its own dropdown.
import { MENU_ITEM_NEUTRAL as ORDER_ACTIONS_MENU_ITEM } from '../components/OrderActionsMenu';
import OrderListPrintMenuItem from '../components/OrderListPrintMenuItem';
import useDeptOrderExpansion from '../hooks/useDeptOrderExpansion';
import {
  OrderTableShell,
  OrderTableRow,
  OrderMobileTableRow,
  ORDER_TABLE_COLUMNS,
  ORDER_COL_WIDTHS_KEY,
  DEPT_ORDERS_MOBILE_TABLE_COLUMNS,
  DEPT_ORDERS_MOBILE_COL_WIDTHS_KEY,
  MAX_ORDER_COLUMN_WIDTH,
  // Task 6 (2026-08-31), Fix Round 1 Finding 1 — the mode decision lives HERE,
  // on the page, via the existing `columns` PROP `OrderTableShell` already
  // took for `MY_ORDERS_TABLE_COLUMNS`. See that export's docblock in
  // `OrderTableView.js` for why this moved out of the shell.
} from '../components/OrderTableView';
import useColumnWidths from '../hooks/useColumnWidths';
import useFitColumnsToScreen from '../hooks/useFitColumnsToScreen';
import Dropdown from '../components/ui/Dropdown';
import { useIsMobileOrderViewport } from '../hooks/useIsMobileOrderViewport';
import useSwipeTabs from '../hooks/useSwipeTabs';
import MobileTabSearchRow, { PHONE_HEADER_TOOLBAR } from '../components/MobileTabSearchRow';
import usePollingRefresh from '../hooks/usePollingRefresh';
import useUnsavedChangesGuard, { UNSAVED_ORDER_CHANGES_MESSAGE } from '../hooks/useUnsavedChangesGuard';
import { useToastOrNoop } from '../contexts/ToastContext';
import OrderSearchAndSort from '../components/OrderSearchAndSort';
import OrderFilterBar, { describeOrderFilters, applyOrderFilterPatch } from '../components/OrderFilterBar';
import Toolbar, { TOOLBAR_DISCLOSURE_TRIGGER } from '../components/ui/Toolbar';
import PageHeaderBand from '../components/ui/PageHeaderBand';
import Tabs from '../components/ui/Tabs';
import FilterChips from '../components/ui/FilterChips';
import useStickyToolbar from '../hooks/useStickyToolbar';
// THE one wording for "who is this order waiting on" — shared with the chevron
// tooltip so the Skip-approvals dialog cannot name the person differently, and
// so the NULL-approver_user_id rule (a step that binds nobody prints no name)
// lives in exactly one place. See its docblock for why `chevronTitle` itself
// cannot serve this caller.
import { awaitingApproverLabel } from '../constants/rowStyles';
import { orderMatchesSearch, withItemsSearchText } from '../utils/orderSearch';
import { collidingIds, withIdSuffix } from '../utils/duplicateLabels';
import { CSV_EXT, CSV_LABEL, CSV_MENU_LABEL } from '../utils/gagCsvExport';
import { withDirtyRowPinned } from '../utils/withDirtyRowPinned';
import { computeTotalPages, clampPage } from '../utils/clampPage';
import { countByTab } from '../utils/tabCounts';
import { compareOrderRows, applySortClick, orderInitialSortDir } from '../utils/tableSort';
import useHeldOpenRows from '../hooks/useHeldOpenRows';
import { usePageView } from '../contexts/PageHeaderContext';
import { PAGE_SIDE_GUTTER } from '../constants/pageLayout';
import { scrollUnderPinnedChrome } from '../hooks/useOpenAtTop';

// The label a production carries in the admin filter. DISPLAY ONLY — never an
// identity. Module scope so it is stable across renders and needs no hook dep.
export const productionLabel = (o) => (
  (o.company_name && o.production_name)
    ? `${o.company_name} - ${o.production_name}`
    : (o.production_name || o.company_name || null)
);

/**
 * The distinct productions present in `orders`, as `{ id, name }`, sorted by
 * label — the option list for the admin production filter.
 *
 * Keyed on `production_id`, NEVER on the label. Exported so the identity rule
 * can be tested without standing up the whole page.
 */
export function buildProductionOptions(orders) {
  const prodMap = new Map();
  orders.forEach(o => {
    if (o.production_id === null || o.production_id === undefined) return;
    const name = productionLabel(o);
    if (name && !prodMap.has(o.production_id)) {
      prodMap.set(o.production_id, name);
    }
  });
  const entries = Array.from(prodMap.entries()).map(([id, name]) => ({ id, name }));
  // Two productions really can share a label. Append the id to just those, so
  // the dropdown stays usable — no warning, no reconciliation prompt; the
  // system is indifferent to the duplicate (product owner, 2026-08-05).
  const colliding = collidingIds(entries.map(e => ({ id: e.id, key: e.name })));
  return entries
    .map(e => ({ id: e.id, name: colliding.has(e.id) ? withIdSuffix(e.name, e.id) : e.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * `sfx_dept_orders_tab` is RETIRED 2026-09-13 by owner ruling ("dep orders
 * shouyld always open on the ALL tab") — neither read nor written by this
 * page. Any value already stored under the key is left in place, never
 * deleted: a stored preference belongs to the user (this page's own
 * principle at the `activeTab` docblock below), the same shape as
 * `sfx_status_filters_dept`.
 */

/* 🔀 `EMPTY_STICKY_IDS` and `QUEUE_MOVE_TOAST_PHRASES` MOVED (2026-10-28) into
   `hooks/useHeldOpenRows.js` with the held row itself — the move toast is now
   shared with My Orders, and its per-status wording reasoning moved with it. */

/**
 * Is this order in the selected production?
 *
 * `productionFilter` holds a `production_id` AS A STRING ('all' = no filter),
 * because that is what a `<select>` value is. It used to hold the production's
 * LABEL and the comparison was a string compare of two names, so selecting one
 * of two same-named productions returned both shows' orders. Never compare
 * production names here.
 */
export function orderInProduction(order, productionFilter) {
  if (productionFilter === 'all') return true;
  if (order.production_id === null || order.production_id === undefined) return false;
  return String(order.production_id) === String(productionFilter);
}

/**
 * 🔀 `withDirtyRowPinned` MOVED to `utils/withDirtyRowPinned.js` on 2026-08-08,
 * the day `Approvals.js` gained the same inline editor and therefore the same
 * hazard (its search box is its one row-removing control). The full reasoning —
 * why pinning rather than N guards, and where the line between pinning and
 * blocking runs — lives in that file's docblock; read it there.
 *
 * Re-exported here because this page's own comments, its test file and
 * CLAUDE.md all cite the name against this module, and a page importing a
 * helper out of ANOTHER page is how two near-copies start.
 */
export { withDirtyRowPinned };


/**
 * One department-list row by id, through `GET /api/orders/department?ids=` —
 * the caller's department scope WITHOUT the unfiltered list's demo/test
 * default (server `parseDepartmentListIds`). Used only for the `?order=` deep
 * link's held row (see `heldOrderIdRef` in the component).
 * @returns {Promise<object|null|undefined>} the row; `null` when the server
 *   answered and the order is not in scope; `undefined` on a failed request.
 */
async function fetchDepartmentRowById(id) {
  try {
    const res = await apiGet(`${API_BASE}/api/orders/department?ids=${id}`);
    if (!res.ok) return undefined;
    const body = await res.json();
    const row = Array.isArray(body) ? body.find((o) => o.id === id) : undefined;
    return row ? withItemsSearchText([row])[0] : null;
  } catch {
    return undefined;
  }
}

function DepartmentOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  /**
   * ── ?order= deep-link (Department Hires "view order") ──────────────────
   * Precedent: `GagBreakdown.js`'s `?gag=` deep-expand. The capture/strip
   * step (read the id, strip it from the URL IMMEDIATELY via
   * `replace: true` so a refresh cannot re-trigger it) is shared with that
   * page through `hooks/useDeepLinkParam.js` — see that file for why
   * `MyTeamTasks.js`'s simpler `?task=` version is deliberately NOT on the
   * same hook. Everything past capture is this page's own: see the
   * resolve effect (after `filteredOrders`) for the pagination-jump /
   * filter-reset / give-up logic — this page paginates, unlike the
   * Breakdown, so "found" alone is not enough to show the row.
   */
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const deepLinkResetDone = useRef(false);
  /**
   * 🔑 **THE DEEP-LINKED ORDER THE LIST DOES NOT CARRY (2026-09-28).** The
   * unfiltered list applies a server-side test/demo default
   * (`testDemoOrderClauses`, server `routes/orders.js`) that the Hires pages
   * producing `?order=` do not, so a hire's "view order" could name an order
   * this list never held and toast "could not be found". The resolve
   * effect now asks `GET /api/orders/department?ids=` — the SAME department
   * scope with the Hires page's demo policy — and a row it returns is HELD:
   * `fetchOrders` re-adds it to every full-array replace (the poll included)
   * until the department changes or the page unmounts.
   * `deepLinkLookupFor` is the id that lookup ran for, so it runs ONCE per link.
   * ⚠️ Neither `withDirtyRowPinned` nor the tick's `heldIds`: both hold an id that
   * is ALREADY in `orders`; this is what keeps it there. A held row is an
   * ordinary member of `orders` — it sits in its own queue tab and is counted.
   */
  const deepLinkLookupFor = useRef(null);
  const heldOrderIdRef = useRef(null);
  const heldOrderRowRef = useRef(null);
  useDeepLinkParam('order', (id) => {
    deepLinkResetDone.current = false;
    deepLinkLookupFor.current = null;
    setPendingOrderId(id);
  });

  // Sticky toolbar. 🔀 `headerTop` IS used now (2026-08-08): the TABLE
  // layout's column headings are a second-level sticky header and pin just
  // beneath the toolbar. It is the MEASURED offset (app bar + real toolbar
  // height), never a constant — the toolbar wraps to two rows on a narrow
  // screen and a guessed number is wrong at exactly those widths.
  const { toolbarRef, headerTop } = useStickyToolbar();
  // Mobile detection: below md breakpoint (768px). Forces page size to 50 on
  // mobile and (2026-10-01) picks the PHONE table and swipe-to-tab.
  const isMobile = useIsMobileOrderViewport();

  // 🔀 The rest of this destructure (`canManageStores`, `activeRole`,
  // `assignments`, `capabilitiesFor`) moved with the expanded row to
  // `hooks/useDeptOrderExpansion.js` (2026-09-27), which reads them there.
  // What stays is what the LIST uses: the admin production filter, the header
  // band and the department-switch dirty registration. (`isBuyer` and
  // `canViewAccounting` fed only the card rows, deleted 2026-10-01.)
  const { isAdmin, activeDepartment, registerDirtyEditGuard } = useDepartment();
  // Shared toast (or a no-op outside the app shell — see useToastOrNoop): the
  // deep link's and the held-row tick's messages.
  const { error: toastError, info: toastInfo } = useToastOrNoop();

  // Production filter (admin only)
  const [productionFilter, setProductionFilter] = useState('all');

  // Filters
  const [searchText, setSearchText] = useState('');
  /**
   * ── THE QUEUE TAB (2026-09-12) — this page's status filter ───────────
   *
   * Owner: stage tabs *"like on admin settings"*, named by QUEUE so no stage
   * doubles, and *"tabs replace the status checkboxes on this page"*. So a
   * SINGLE tab id stands where `selectedStatuses` (a seven-member multi-select)
   * and `showCompleted` (a boolean) both used to be.
   *
   * 🔴 **OPENS ON `all` EVERY VISIT — OWNER RULING, 2026-09-13, VERBATIM:
   * "dep orders shouyld always open on the ALL tab".** `sfx_dept_orders_tab`
   * is RETIRED (see the retirement note at its old declaration site, above):
   * this seed is a plain literal, never a storage read, and `ui/Tabs`'s own
   * mount adoption never fires either, because no `persistKey` is passed.
   *
   * 🪤 **`sfx_status_filters_dept` AND `sfx_dept_orders_hide_completed` ARE NOW
   * IGNORED ON THIS PAGE — NEITHER IS READ, NEITHER IS WRITTEN, AND NEITHER IS
   * DELETED.** A stored preference belongs to the user: clearing it would also
   * clear it for anyone who lands back on a version of this page that reads it,
   * and deleting somebody's saved filter is not this change's to do. Both keys
   * are left in place. (🔻 `MyOrders.js`' own keys, `sfx_status_filters_my` /
   * `sfx_my_orders_hide_completed`, went unread too on 2026-09-30 — its tabs
   * replaced them.) Pinned by §6 of `DepartmentOrders.queueTabs.test.js`.
   */
  // 🔀 2026-10-13: still not stored, but a browser Back/Forward returns to the
  // queue tab its history entry was on (`hooks/useViewInHistory`). A fresh
  // visit, a link and a reload open on All, as before.
  const [activeTab, setActiveTab] = useHistoryState('dept-orders-tab', 'all', (v) => ORDER_QUEUE_TAB_IDS.includes(v));
  // 🔑 Publish the queue tab to the page-view logger (2026-09-17). ONE LINE, and
  // it must stay one line: this page holds its tab in plain state, so without a
  // published value the logger can only ever record "/department/orders" and the
  // owner's *"which tabs"* question has no answer. It writes nothing itself —
  // `hooks/usePageViewLogger.js` remains the only writer of a row.
  usePageView(activeTab);
  /**
   * ── THE HELD ROW AFTER A TICK (2026-09-12; shared hook 2026-10-28) ─────
   *
   * Product owner, on staging: *"the order close as soon as its ticked which
   * seems a little jarring on the eyes"*, and again 2026-10-28 from a phone:
   * *"it should have stayed open as I wanted to tick of multiple deliveries"*.
   * Ticking auto-advances the server status (`ordered`/`partially_delivered` →
   * `delivered`, and back again on an untick), the tick's own list refetch
   * lands, and the row's `orderQueueTab` no longer matches the tab the user is
   * standing on — so it was filtered away mid-click.
   *
   * 🔑 **THE TICK STAYS IMMEDIATE.** A Save button was considered and REJECTED
   * by the owner: it re-introduces dirty state under the 30 s poll and brings
   * back the receive-all wrong-target race `utils/receivedItems.js`'s
   * `onDetailInvalidated` closes. So the row is held on screen instead — it
   * repaints from the refreshed payload (pill → "Delivered", progress bar
   * full, `DeliveredOrderPill`'s "Ready to complete").
   *
   * The rule — linger not membership (the counts stay truthful, so
   * `To receive (3)` can briefly show FOUR rows), never on `all`, released
   * only when the user closes the last open row / changes a filter / leaves,
   * NEVER by the poll — is `hooks/useHeldOpenRows.js`'s, shared with My Orders.
   *
   * 🚨 **THE `completed` TAB IS IN SCOPE TOO — MEASURED, NOT ASSUMED, AND THE
   * OBVIOUS GUESS IS WRONG.** It is tempting to say completion is an overlay
   * on a separate column so a tick cannot move a completed row. It can:
   * un-ticking the LAST received item of a `delivered` + completed order makes
   * `server/helpers/deliveryStatus.js#deriveDeliveryStatus` answer `ordered`,
   * `ordered` is not in `COMPLETABLE_STATUSES`
   * (`server/helpers/orderCompletionClear.js:95` — `['delivered',
   * 'partially_delivered']`), and the per-item route calls
   * `clearCompletionIfNotCompletable` UNCONDITIONALLY inside the same
   * transaction, so `completed_at` is wiped and the row lands in `To receive`.
   * Un-ticking only SOME items keeps it `partially_delivered`, which IS
   * completable, so that case really does stay put. Both directions fall out
   * of the generic code; neither is special-cased.
   *
   * ⚠️ NOT `withDirtyRowPinned`, despite both being called pinning. That one
   * holds a DIRTY row against PAGINATION and is deliberately excluded from
   * `filteredOrders`; this one holds a CLEAN row against the TAB and is inside
   * it. Do not merge them.
   */
  const { heldIds, holdThrough, releaseWhenAllClosed, tickBusy } = useHeldOpenRows({
    activeTab,
    unfiltered: activeTab === 'all',
    filterKey: [searchText, productionFilter],
    tabOf: orderQueueTab,
    tabs: ORDER_QUEUE_TABS,
    announce: toastInfo,
  });
  /* 🔻 `showCompleted` AND ITS `sfx_dept_orders_hide_completed` READ/WRITE ARE
     GONE FROM THIS PAGE (2026-09-12) — completion is the `Completed` TAB now,
     and the owner folded the boolean and its "Completed hidden" chip into it.
     🪤 The key is IGNORED, never deleted: see the tab state above. (`MyOrders.js`
     dropped its pill for tabs on 2026-09-30.) */
  /* 🔻 `filterMode` AND THE All / Actionable TOGGLE ARE GONE FROM THIS PAGE
     (2026-09-12, owner ruling). Pete: *"Actionable is not necessarily my task —
     a supervisor with a buyer does not want to see it for To order"*.
     🔑 **THE ARGUMENT, so nobody re-adds it as a convenience.** "Actionable" is
     the server's `can_action`, which answers *could you act*. For a supervisor
     holding `canManageDepartmentOrders` that includes every `approved` order the
     BUYER is meant to place — so the toggle, and the `· N actionable` count
     beside it, were a personal-sounding label over somebody else's work. The
     rename from *"awaiting your action"* on 2026-09-11 fixed the grammar and left
     that premise standing; this removes the premise.
     🔑 What replaces it is STRUCTURE, not a label: the queue tabs give a buyer
     `To order` / `To receive` and a supervisor `To approve`, and the viewer's OWN
     steps live on My Approvals and are marked by the green chevron.
     ⚠️ **`canActionOrder` IS UNTOUCHED and has five other readers** —
     `canApproveReject`, both `getNextStepInfo` call sites, and `actionable=` on
     BOTH row components (the chevron). Deleting it, or the chevron with it, is
     NOT part of this ruling: the chevron claims only the viewer's own step.
     🔁 A "mine only" switch may return ONLY if a per-department "buying is
     delegated" preference is ever built — the owner's own condition — and it
     would be a DIFFERENT predicate, not this one restored.
     🟢 There was never an `sfx_` key for this: `filterMode` was plain state,
     never persisted, so nothing is left stranded in anybody's localStorage. */

  // Sorting
  const [sortField, setSortField] = useState('order_date');
  const [sortDir, setSortDir] = useState('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(getStoredPageSize);

  /* 📱 ALWAYS A TABLE SINCE 2026-10-01 (owner: *"Dept Orders + To Approve:
     phone TABLE instead of cards"*). ≥768px is the twelve-column table it has
     been since 2026-09-12; below `md` it is the PHONE table
     (`DEPT_ORDERS_MOBILE_TABLE_COLUMNS`, `OrderMobileTableRow`) — the My
     Orders model. The CARDS path is deleted from this page, and with it the
     layout hook: there was no toggle here since 2026-09-12, so the layout was
     a pure function of the viewport, and both answers are now a table. The
     other two order pages followed on 2026-10-18 (cards retired).
     🔑 `isPhoneTable` tracks the
     media query LIVE (`useIsMobileOrderViewport`), so a resize across 768px
     swaps the column set rather than keeping the mount-time answer. */
  const isPhoneTable = isMobile;

  // Export state (admin only)
  const [exporting, setExporting] = useState(false);

  /* 🔻 The `statuses` array (the seven-member pill order) went with the status
     multi-select on 2026-09-12. (`MyOrders.js` dropped its own 2026-09-30.) */

  /**
   * ── User-draggable column widths (2026-08-10) ────────────────────────
   *
   * Product owner: *"to approve and department order tables need the ability to
   * have their column widths adjusted like breakdown."*
   *
   * 🔑 **THE STATE LIVES ON THE PAGE, NOT IN `OrderTableShell`, for exactly the
   * reason `GagBreakdown.js` records: `Reset column widths` is an `Actions ▾`
   * item in the header band.** The shell renders ONE table and would happily
   * hold the map itself, but the menu would then need a ref or a callback
   * registration to reach `resetAll` — and a second route into one piece of
   * state is how one width model quietly becomes two.
   *
   * 🪤 **`sfx_order_table_col_widths` is passed as a CONSTANT, so
   * `npm run check-bloat` cannot see it** in either direction — its orphan-key
   * scan matches string LITERALS only. Grep the key by hand. The reasoning is
   * written once, at the constant.
   */
  const {
    widths: columnWidths,
    setWidth: setColumnWidth,
    resetColumn: resetColumnWidth,
  } = useColumnWidths(ORDER_TABLE_COLUMNS, ORDER_COL_WIDTHS_KEY, MAX_ORDER_COLUMN_WIDTH);
  /* 🔑 THE PHONE TABLE is a different COLUMN SET, so it has its own width
     model and key (`DEPT_ORDERS_MOBILE_COL_WIDTHS_KEY`) — both hooks are always
     called (hook order), and only the active pair reaches the shell. */
  const {
    widths: mobileColumnWidths,
    setWidth: setMobileColumnWidth,
    resetColumn: resetMobileColumnWidth,
  } = useColumnWidths(DEPT_ORDERS_MOBILE_TABLE_COLUMNS, DEPT_ORDERS_MOBILE_COL_WIDTHS_KEY, MAX_ORDER_COLUMN_WIDTH);
  const activeTableColumns = isPhoneTable ? DEPT_ORDERS_MOBILE_TABLE_COLUMNS : ORDER_TABLE_COLUMNS;
  const activeColumnWidths = isPhoneTable ? mobileColumnWidths : columnWidths;
  const activeSetColumnWidth = isPhoneTable ? setMobileColumnWidth : setColumnWidth;
  const activeResetColumnWidth = isPhoneTable ? resetMobileColumnWidth : resetColumnWidth;

  /**
   * ── `Fit columns to screen` (owner ruling 2026-09-28, applying the
   * Breakdown's 2026-09-27 ruling here) ─────────────────────────────────
   *
   * *"reset column widths should use the same code and changes as Fit
   * columns to screen on the Breakdown page. same for other tables."*
   * REPLACES `Reset column widths` — same menu slot, same testid family
   * renamed, same "put everything back" affordance except it now fits the
   * table to the screen rather than reverting to the measured defaults.
   *
   * 🔑 **The available width is the table's OWN measurement**, reported up
   * by `OrderTableShell`'s `onRegionWidthChange` (which forwards
   * `PageScrollTable`'s own measured scrollport width — the SAME number
   * `wrapRows`' rows already read via `OrderTableWidthContext`, just a
   * second route to it since the page needs it too). `null` = no table on
   * screen to fit, exactly the Breakdown's rule.
   *
   * 🔑 **ONE write path** — `useFitColumnsToScreen` calls `setColumnWidth`
   * once per column, batched into one render and one storage write. See
   * `hooks/useFitColumnsToScreen.js`.
   */
  const [tableRegionWidth, setTableRegionWidth] = useState(null);
  const { fitColumnsToScreen, canFitColumns, alreadyFit: columnsAlreadyFit } = useFitColumnsToScreen({
    columns: activeTableColumns,
    widths: activeColumnWidths,
    setWidth: activeSetColumnWidth,
    maxWidth: MAX_ORDER_COLUMN_WIDTH,
    available: tableRegionWidth,
  });

  /**
   * 🔑 **The fit item is offered only where the widths are USED.** They apply
   * to the table alone, so a
   * menu row that fit something the user cannot see would be a control that
   * appears to do nothing. Same shape as `GagBreakdown`'s `canResetColumns`,
   * which is gated on the VIEW rather than on a permission for the same reason.
   * 🔀 Always `true` since 2026-10-01: this page has no cards any more (on a
   * phone it fits the PHONE column set — `activeTableColumns`).
   */
  const canResetColumns = true;

  /**
   * ⚠️ **`PageHeaderBand` decides with `Boolean(actions)`, and a fragment that
   * renders nothing is STILL TRUTHY** — so the slot must be a conditional
   * expression yielding `undefined`, never a fragment that can come out empty.
   *
   * 🔻 **AS OF 2026-08-10 THE CONDITION IS UNCONDITIONALLY TRUE, AND IT IS
   * WRITTEN OUT RATHER THAN DELETED.** It was `isAdmin || canResetColumns`; the
   * menu now always holds at least `Print all`, which is ungated and offered in
   * both layouts, so there is no viewer for whom this menu is empty. **The
   * constant is kept — with the rule beside it — because the moment somebody
   * gates `Print all`, or the last unconditional item leaves this menu, the slot
   * can go empty again and `PageHeaderBand` will draw a stray flex child for
   * every affected viewer.** A `true` with the reasoning attached is cheaper than
   * rediscovering the rule; do NOT "simplify" the slot to an unconditional
   * expression, which is the form that loses it.
   */
  const hasHeaderActions = true;


  // Load cost codes for the active department
  //
  // 🔻 An `|| isAllDepartments` disjunct on the early return below, and a
  // per-ORDER cache (`hooks/useAllModeAccountingLists.js`) beside it, went
  // with the All Productions mode on 2026-08-31. They existed because in that
  // mode `activeDepartment.department_id` was a sentinel string that 403s
  // these endpoints, and because one page-level list cannot answer for the
  // several departments a row could belong to. With one department in view,
  // this effect is that answer again — which is what it was before the mode.

  // `silent` skips the loading flip so a post-save refresh does not unmount the
  // whole list and flash. The convention for expand-to-edit rows is "collapse
  // first (animated), then fetchData(true) silently in background".
  /**
   * @param {boolean} silent  suppress the SPINNER (a refetch behind a mounted list)
   * @param {object}  [opts]
   * @param {boolean} [opts.suppressErrors=false]  suppress the ERROR BANNER too.
   * @returns {Promise<Array|null>} the rows just loaded, or `null` on a
   *   failure that was swallowed. **Additive** — every pre-existing caller
   *   ignores it. The tick's refresh is the one caller that needs the payload
   *   IN HAND: it has to compare the refreshed row's queue against the tab the
   *   user was standing on, and reading `orders` back from state in the same
   *   turn would see the PREVIOUS list (it is set, not read, above).
   *
   * 🔻 **`opts.keepSticky` IS DELETED (2026-10-28)** — this function no longer
   * releases the held row at all. It used to clear it on every call but the
   * tick's own, so the 30 s poll dropped a row the storeman still had OPEN.
   * The hold is now released only by the user (`hooks/useHeldOpenRows.js`).
   *
   * 🔑 **`silent` AND `suppressErrors` ARE TWO DIFFERENT THINGS, and conflating
   * them is what would have made the 30s poll user-hostile.** `silent` was
   * always about the loading spinner; it still calls `setError` on failure, and
   * it should — a refetch the user TRIGGERED (saving an edit, retrying) must say
   * so when it fails. A POLL is different: the user did not ask for that
   * request, so a dropped connection must not paint an error banner over
   * perfectly good rows they are reading. See `hooks/usePollingRefresh.js` rule
   * 4. `setOrders` is still applied on success either way, which is the whole
   * point of the poll.
   */
  const fetchOrders = useCallback(async (silent = false, opts = {}) => {
    const { suppressErrors = false } = opts;
    try {
      if (!silent) setLoading(true);
      const response = await apiGet(`${API_BASE}/api/orders/department`);
      if (!response.ok) throw new Error('Failed to fetch orders');
      // `items_search_text` is rebuilt HERE from `preview_items` — the server
      // stopped sending it (2026-09-26 payload trim); see
      // `utils/orderSearch.js#itemsSearchText`. This is the only whole-array
      // write on the page, so every row that can be searched passed through it.
      let data = withItemsSearchText(await response.json());
      // The deep-linked row this list does not carry — see `heldOrderIdRef`.
      // A network failure keeps the last copy rather than dropping the row;
      // an empty answer (deleted / moved) releases it.
      const heldId = heldOrderIdRef.current;
      if (heldId != null && !data.some((o) => o.id === heldId)) {
        const held = await fetchDepartmentRowById(heldId);
        if (held === null) heldOrderIdRef.current = null;
        else {
          if (held) heldOrderRowRef.current = held;
          if (heldOrderRowRef.current) data = [...data, heldOrderRowRef.current];
        }
      }
      setOrders(data);
      setError(null);
      return data;
    } catch (err) {
      if (!suppressErrors) setError(err.message);
      // Rethrow so a poll's own logging sees it; the hook swallows it.
      if (suppressErrors) throw err;
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
    // 🔀 **`activeDepartment?.department_id` IS THE WHOLE OF THE REFETCH ON A
    // DEPARTMENT SWITCH (2026-08-17).** `GET /api/orders/department` is scoped
    // SERVER-SIDE by the `x-department-id` header, which `utils/api.js` reads
    // from localStorage at CALL time — so the request is always correctly
    // scoped and only its TIMING could be wrong. `switchDepartment` is
    // client-side and reloads nothing (`contexts/DepartmentContext.js`), so
    // without this dep the mount effect below fires ONCE and the page keeps
    // showing the PREVIOUS department's orders until the 30s poll happens to
    // fire. Depending on the id changes this callback's identity on a switch,
    // which re-runs that effect. This is the twin of the dep already carried by
    // `Approvals.fetchPendingApprovals` and `MyOrders.fetchOrders`.
    // 🪤 `department_id`, NEVER `.id` — `activeDepartment` never carries `id`.
    // The body does not read it (the header does); it is a dependency, not a
    // reference — the same shape those two siblings already ship, eslint-clean
    // (an unused member-expression dep is not flagged by exhaustive-deps).
  }, [activeDepartment?.department_id]);

  // A department switch releases the deep-link's held row (`heldOrderIdRef`):
  // it belonged to the department the link was opened in.
  useEffect(() => {
    heldOrderIdRef.current = null;
    heldOrderRowRef.current = null;
  }, [activeDepartment?.department_id]);

  // After a received change, refresh the LIST: the collapsed row's Delivery
  // Progress bar reads the list payload's `delivery_info` and the status badge
  // reads `status`, neither of which the detail-cache refresh (done by the
  // shared received handlers in `utils/receivedItems.js` before this fires)
  // touches. The status can auto-advance to partially_delivered/delivered, so
  // a list refetch is required.
  //
  // 📌 It goes through `holdThrough` (`hooks/useHeldOpenRows.js`, shared with
  // My Orders since 2026-10-28): the row the user is ticking is HELD on its tab
  // while it is open, and its move is announced once. The two mid-flight filter
  // checks, the `all` early return and the toast wording all live there now.
  const handleItemsReceived = useCallback(
    (orderId) => holdThrough(orderId, () => fetchOrders(true)),
    [holdThrough, fetchOrders]
  );

  // ── THE EXPANDED ROW (2026-09-27) ─────────────────────────────────────────
  // State, handlers, dialogs and `renderExpanded(order)` moved VERBATIM to
  // `hooks/useDeptOrderExpansion.js` so Department Hires can expand a hire into
  // the same panel. This page keeps the LIST: `orders`, `fetchOrders`, the
  // poll, the leave guard, the deep link and the held row — each reading
  // `editDirtyId` from the hook, so there is one source of truth.
  // `handleItemsReceived` above is the tick cluster's list refresh.
  const {
    expandedOrders,
    collapseAllOrders,
    orderDetails,
    detailsLoading,
    editDirtyId,
    currentUser,
    currentRole,
    canActionOrder,
    continueWhenClean,
    toggleExpand,
    renderExpanded,
    dialogs: expansionDialogs,
  } = useDeptOrderExpansion({
    setOrders,
    fetchOrders,
    // Always a table since 2026-10-01 (desktop AND phone) — the inline editor
    // is offered on both.
    isTableLayout: true,
    setSuccessMessage,
    onListRefresh: handleItemsReceived,
    onTickBusyChange: tickBusy,
  });
  // The held row is released when the user closes the last open row — never
  // by a list reload; see `hooks/useHeldOpenRows.js`.
  useEffect(() => releaseWhenAllClosed(expandedOrders), [expandedOrders, releaseWhenAllClosed]);

  // A browser Back/Forward reopens the order this entry had open (2026-10-13),
  // through the `?order=` deep link's own request, so its tab and page are
  // found the same way. Clean rows only: see `useOpenRowInHistory`.
  useOpenRowInHistory('dept-orders-open', expandedOrders.size === 1 ? [...expandedOrders][0] : null, (id) => {
    deepLinkResetDone.current = false;
    deepLinkLookupFor.current = null;
    setPendingOrderId(id);
  });

  /**
   * 🔄 THE 30-SECOND BACKGROUND POLL (2026-08-08). Product owner: *"add a 30
   * second poll"* — the direct follow-up to removing `Refresh` from this page.
   * Without it a full browser reload was the only way to see an order someone
   * else had raised, and a reload discards expanded rows and any in-progress
   * inline edit.
   *
   * 🚨 **`paused` IS THE DIRTY GUARD AND IT IS THE WHOLE REASON THIS IS NOT A
   * BARE `setInterval`.** `editDirtyId` is set while an expand-to-edit form
   * holds unsaved changes; a refetch landing then re-renders the list and can
   * discard typing the user did nothing to cause. That exact failure already
   * happened on this page through a cross-tab `storage` listener (retired with
   * the layout toggle, 2026-10-18), and the accepted fix was to DEFER.
   * Ticks during an edit are skipped, not queued: the next natural tick after
   * the flag clears refreshes, and catching up would fire a burst the moment a
   * long edit was saved.
   *
   * The visibility pause, the in-flight guard and the silent-error rule all
   * live in the hook — read its docblock before changing any of them.
   */
  usePollingRefresh(
    useCallback(() => fetchOrders(true, { suppressErrors: true }), [fetchOrders]),
    { paused: editDirtyId !== null }
  );

  // The mount fetch. `fetchOrders` now changes identity on a department switch
  // (its `activeDepartment?.department_id` dep), so this effect re-runs and
  // re-issues the list request for the newly active department — no manual
  // reload needed. It still fires the initial load when `currentUser` resolves.
  useEffect(() => {
    if (currentUser) fetchOrders();
  }, [fetchOrders, currentUser]);

  // 🔀 The DEPARTMENT-SWITCH clear of the expand/edit state moved with that
  // state to `hooks/useDeptOrderExpansion.js` (2026-09-27); both owner rulings
  // (2026-08-30) travelled with it and still govern it.

  // ── REGISTER THIS PAGE'S DIRTY STATE WITH THE SWITCH GUARD (2026-08-30) ──
  // `DepartmentContext.js`'s `hasDirtyEdit()` is what
  // `ProductionDepartmentSwitcher.js` reads BEFORE calling `switchDepartment`,
  // to decide whether to raise the "you'll lose it" dialog above. Kept in sync
  // via effect (not read directly, since the switcher has no view of this
  // page's state) and explicitly cleared on unmount — leaving it `true` past
  // a route change would block every future switch with a dialog about an
  // edit that no longer exists.
  useEffect(() => {
    registerDirtyEditGuard?.(editDirtyId !== null);
    return () => registerDirtyEditGuard?.(false);
  }, [editDirtyId, registerDirtyEditGuard]);

  // Leaving the PAGE with a dirty inline edit — sidebar, header, Back, reload
  // (R14, 2026-09-15; `hooks/useUnsavedChangesGuard.js`). Replaces the
  // `beforeunload` that lived in `OrderInlineEditor`. ONE guard for the page.
  // Queue tabs / deep-linked rows change only the search string: no prompt.
  const leaveGuard = useUnsavedChangesGuard({
    when: editDirtyId !== null,
    message: UNSAVED_ORDER_CHANGES_MESSAGE,
  });


  /* 🔻 `handleLayoutChange` IS RETIRED (2026-09-12), not left inert. It
     existed to refuse a Cards ⇄ Table switch while an inline edit was dirty —
     switching to Cards makes `isTableLayout` false, `expandedContent` becomes
     the read-only panel and the editor unmounts, while the row STAYS EXPANDED
     so it does not even look like anything was lost. **That rule is NOT
     repealed**, it simply has no subject any more: the toggle was retired
     from all three order pages (2026-10-18), so there is no control and no
     `setLayout` in scope — keeping a dead handler would advertise a guard
     over a gesture that cannot be made.
     ⚠️ The dirty guard itself STAYS, as `continueWhenClean` (2026-09-28; was
     `blockIfDirty`): expand-another-row, the navigating row actions, the
     navigate-away title link and the queue tabs. */




  // Options for the admin production filter. Identity is `production_id`; the
  // label used to BE the identity, and the captured id was never used — so two
  // productions sharing a name (legitimate and expected: any buyer may create
  // one, and duplicates are normal) produced ONE option, and picking it
  // returned BOTH shows' orders. That is cross-show order visibility arising
  // from a name collision.
  const productionOptions = useMemo(
    () => (isAdmin ? buildProductionOptions(orders) : []),
    [orders, isAdmin]
  );

  const matchesProductionFilter = useCallback(
    (o) => !isAdmin || orderInProduction(o, productionFilter),
    [isAdmin, productionFilter]
  );

  /**
   * The one-line statement of what is filtered, printed on the PDF.
   *
   * 🔑 **IT IS BUILT FROM `describeOrderFilters`' CHIP LABELS, NOT HAND-WRITTEN,
   * and that is the whole reason it can be trusted.** The chip row is already the
   * app's answer to *"what is narrowing this list?"* — CLAUDE.md calls it the
   * mitigation for burying filters behind a disclosure, because a narrowed list
   * looks identical to a short one. **A sheet of paper has exactly that problem
   * and no chip row**, so the printed page reuses the same describer rather than
   * growing a second description that can disagree with the screen.
   *
   * ⚠️ **SEARCH IS APPENDED SEPARATELY because it is deliberately NOT a chip** on
   * any surface (it has its own always-visible input). On paper there is no input
   * to look at, so omitting it would let a search-narrowed list print as
   * unfiltered — the one place the no-search-chip rule must not be copied.
   */
  const printFilterSummary = useMemo(() => {
    /* 🚨 **THE TAB IS APPENDED SEPARATELY, FOR THE SAME REASON SEARCH IS.**
       `describeOrderFilters` describes CHIPS, and the queue tab is deliberately
       not one — it is always visible above the toolbar, so a chip would be a
       second control over one piece of state. On PAPER there is no strip to
       look at, so omitting it would let a single-queue sheet print as though it
       listed the whole department. Same exception the search box already
       carries, and the only one. */
    const parts = describeOrderFilters({
      statuses: [],
      selectedStatuses: [],
      productionFilter,
      productionOptions,
    }).map((c) => c.label);
    if (activeTab !== 'all') {
      const tab = ORDER_QUEUE_TABS.find((t) => t.id === activeTab);
      parts.unshift(`Queue: ${tab ? tab.label : activeTab}`);
    }
    if (searchText) parts.push(`Search: "${searchText}"`);
    return parts.join(' · ');
  }, [activeTab, productionFilter, productionOptions, searchText]);

  // Filter and sort
  const filteredOrders = useMemo(() => {
    let result = [...orders];

    // Production filter (admin only)
    if (isAdmin && productionFilter !== 'all') {
      result = result.filter(matchesProductionFilter);
    }

    /* ── THE QUEUE TAB (2026-09-12) ──────────────────────────────────────
       🔑 **`all` APPLIES NO PREDICATE AT ALL** — "All" is everything, including
       the rejected and cancelled orders that, since the afternoon reversal of
       2026-09-12, ALSO have their own `Cancelled` tab — plus any row whose
       status `orderQueueTab` does not recognise, which is the only `null` left.
       Testing `orderQueueTab(o) === 'all'` instead would empty that tab
       completely.
       🔑 It replaces BOTH the status multi-select and the Completed pill:
       completion is a TAB now, so there is no second, orthogonal completion
       filter left to compose with. */
    /* 🔴 **`|| heldIds.has(o.id)` IS THE HELD ROW AFTER A TICK
       (2026-09-12; `hooks/useHeldOpenRows.js` since 2026-10-28)** — the row
       the user is ticking lingers here even though the auto-advance has moved
       its queue. It is a LINGER, not a membership:
       `tabCounts` never consults this set, so the strip keeps telling the truth
       about the queue while the screen keeps the row the user is looking at.
       See the hook's docblock for who fills it and what empties it.
       🪤 It is ORed into the TAB predicate only — never into the production
       filter or the search below. A held row that survived a search would be
       a row matching nothing the user typed. */
    if (activeTab !== 'all') {
      result = result.filter((o) => orderQueueTabs(o).includes(activeTab) || heldIds.has(o.id));
    }

    // Search — shared utility covers reference_number, po_number,
    // requester, supplier, dept/production, notes, line items (personal_ref
    // came off the list 2026-08-12, see orderSearch.js).
    if (searchText && searchText.trim()) {
      result = result.filter(o => orderMatchesSearch(o, searchText));
    }

    // Sort — THE order-list comparator, shared with My Orders and To Approve
    // (`utils/tableSort.js`; every case and its reasoning live there).
    result.sort((a, b) => compareOrderRows(a, b, sortField, sortDir));

    return result;
  }, [orders, activeTab, searchText, sortField, sortDir, isAdmin, productionFilter, matchesProductionFilter, heldIds]);

  // Pagination — on mobile, force effective page size to 50 regardless of stored preference
  const effectivePageSize = isMobile ? 50 : pageSize;
  const totalPages = computeTotalPages(filteredOrders.length, effectivePageSize);
  // 🔑 CLAMPED (root CLAUDE.md's Pagination section, `utils/clampPage.js`): a
  // refetch that SHRINKS `filteredOrders` must never strand the view past
  // its last page. `page` is DERIVED, not written back into `currentPage`
  // state — see `clampPage.js`'s docblock for why an effect was deliberately
  // not added here. It feeds both the slice below and the Pagination
  // `currentPage` prop, so the two can never disagree.
  const page = clampPage(currentPage, totalPages);
  const pageOfOrders = effectivePageSize === 'ALL' ? filteredOrders : filteredOrders.slice((page - 1) * effectivePageSize, page * effectivePageSize);
  // A row holding unsaved inline edits cannot be sliced away — see
  // `withDirtyRowPinned` for why this is one choke point rather than six
  // guards. Note `totalPages` above and the Pagination `totalItems` below stay
  // on `filteredOrders`: the pinned row is an extra held on screen, not a
  // member of the filtered set, and counting it would make the pager lie.
  const paginatedOrders = withDirtyRowPinned(pageOfOrders, orders, editDirtyId);

  // 🔀 The held-row release on these same three filters moved INTO
  // `hooks/useHeldOpenRows.js` (2026-10-28) — its `filterKey` is this list.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, activeTab, productionFilter]);

  /**
   * Resolve the pending `?order=` deep-link once the department's order list
   * has loaded.
   *
   * 🚨 **THIS EFFECT MUST STAY DECLARED AFTER THE PAGE-RESET EFFECT ABOVE**
   * (the one whose entire body is `setCurrentPage(1)`, keyed on
   * `[searchText, activeTab, productionFilter]`). React
   * runs effects in HOOK-DECLARATION ORDER within one render pass, so when
   * this effect's own reset branch below changes those same filter values,
   * BOTH effects re-fire in the same pass — the page-reset effect first,
   * writing `currentPage = 1`, then this one, computing the real target page
   * and overwriting it. Declared in the other order, the page-reset effect
   * would run SECOND and silently snap `currentPage` back to 1 after this
   * effect had already jumped it forward — the row would be expanded in
   * state but not rendered, because pagination slices `filteredOrders` by
   * `currentPage`. This only bites when a deep link BOTH requires a filter
   * reset AND resolves to page 2 or later; a fixture with one order, or one
   * that needs no reset, cannot see it — see the "reset AND jump" case in
   * `DepartmentOrders.deepLinkOrder.test.js`, whose mutation proof is
   * reordering these two effects.
   *
   * 🪤 THE TRAP: `GET /api/orders/department` (`fetchOrders` above) returns
   * the department list WITHOUT the page's client filters — so a listed target
   * is already in `orders` — but `filteredOrders` narrows it through the status pills, the
   * queue TAB (an off-hired hire's order is almost
   * always delivered/completed, and many buyers persist it hidden by
   * default), Actionable-only and search. And unlike `GagBreakdown.js`, THIS
   * page also PAGINATES — a row that survives every filter can still be on a
   * page nobody is looking at. A naive expand-by-id over only the rendered
   * (current-page) rows would silently show nothing for exactly the common
   * case: "I clicked an old returned hire".
   *
   * Not found in the CURRENT filter → reset every filter to its default
   * ONCE (`deepLinkResetDone` guards the loop), tell the user it happened
   * (owner: the reset must be VISIBLE, not mysterious — a deliberate
   * departure from the silent `?gag=` precedent), and let `filteredOrders`
   * recompute on the next render. No refetch is needed — this list is
   * already client-filtered from the full department set fetched once.
   * Still not found after the reset → give up with a toast and clear the
   * pending id. Never leave it silently pending.
   *
   * 🔻 **"THE TARGET IS ALREADY IN `orders`" IS FALSE FOR A SERVER-FILTERED
   * ORDER (corrected 2026-09-28).** The unfiltered list drops `is_test`
   * orders (and `is_demo` ones outside a seat) server-side, while the Hires
   * pages link to them — so an order the list never carried is looked up ONCE
   * through `?ids=` and held (`heldOrderIdRef`) before the give-up toast.
   *
   * Found → jump `currentPage` to the page holding its index in
   * `filteredOrders`, expand it via the SAME `toggleExpand` the row's own
   * chevron uses (reuses its existing cache-on-first-open
   * `GET /api/orders/:id` fetch — no second fetcher is added here), and
   * scroll it into view after paint.
   *
   * ⚠️ jsdom has no layout, scroll or paint, and does not even implement
   * `Element.prototype.scrollIntoView` — whether the row is actually visible
   * to the user afterward is an unverified BROWSER claim, not something the
   * client test suite can prove. See `DepartmentOrders.deepLinkOrder.test.js`.
   */
  useEffect(() => {
    if (pendingOrderId == null || loading || error) return;
    const idx = filteredOrders.findIndex((o) => o.id === pendingOrderId);
    if (idx === -1) {
      /* 🔑 **IT SELECTS THE ORDER'S OWN QUEUE, NOT `all`** (owner ruling,
         2026-09-12): the row must be VISIBLE *and* IN CONTEXT, so a deep link
         to a delivered hire lands on `To complete` with that queue's other work
         beside it rather than dumping the reader at the top of the whole
         department. 🔁 **A deep link to a rejected or cancelled order lands on
         `Cancelled` now** — it landed on All until the afternoon reversal of
         2026-09-12 gave the pair a tab. 🪤 The `|| 'all'` fallback therefore
         fires only for a status `orderQueueTab` does not recognise: it is the
         fail-quiet landing, not defensive padding.

         🚨 **THE `target` LOOKUP IS LOAD-BEARING AND ITS ABSENCE WAS A REAL
         DEFECT THIS CHANGE WOULD OTHERWISE HAVE INTRODUCED.** The reset branch
         used to rely on `setSelectedStatuses([...statuses])` handing this effect
         a NEW ARRAY, which changed `filteredOrders`' identity and re-ran the
         effect so the give-up branch below could fire. A single scalar tab does
         not do that: for an order that is not in `orders` AT ALL (deleted, or in
         another department) every setter would be a no-op, nothing would
         re-render, and the deep link would fail SILENTLY — measured, §4 of
         `DepartmentOrders.deepLinkOrder.test.js` went red with 0 toasts.
         Asking whether the order exists before resetting anything removes the
         dependence on state churn, and is strictly better than what it replaces:
         a deleted order no longer clears the reader's filters and no longer
         toasts *"Filters were cleared"* immediately before *"could not be
         found"*. */
      const target = orders.find((o) => o.id === pendingOrderId);
      if (target && !deepLinkResetDone.current) {
        deepLinkResetDone.current = true;
        setActiveTab(orderQueueTab(target) || 'all');
        /* 🔻 `setFilterMode('all')` was here and went with the toggle
           (2026-09-12). There is no view mode left to reset on this page. */
        setSearchText('');
        if (isAdmin) setProductionFilter('all');
        toastInfo('Filters were cleared to show this order.');
        return;
      }
      /* Not in the list at all → ask `?ids=` ONCE before giving up (see
         `heldOrderIdRef`). A returned row is held and appended; the `orders`
         change re-runs this effect, which then resolves it like any listed
         row. The give-up toast below fires from here only for an order the
         lookup did not return either. */
      if (!target && deepLinkLookupFor.current !== pendingOrderId) {
        const wanted = pendingOrderId;
        deepLinkLookupFor.current = wanted;
        fetchDepartmentRowById(wanted).then((row) => {
          if (deepLinkLookupFor.current !== wanted) return;
          if (row) {
            heldOrderIdRef.current = wanted;
            heldOrderRowRef.current = row;
            setOrders((prev) => (prev.some((o) => o.id === wanted) ? prev : [...prev, row]));
            return;
          }
          setPendingOrderId(null);
          toastError('That order could not be found — it may have been deleted or moved to a different department.');
        });
        return;
      }
      if (!target) return; // the lookup above is still in flight
      setPendingOrderId(null);
      toastError('That order could not be found — it may have been deleted or moved to a different department.');
      return;
    }
    const targetId = pendingOrderId;
    const size = effectivePageSize;
    const page = size === 'ALL' ? 1 : Math.floor(idx / size) + 1;
    setCurrentPage(page);
    if (!expandedOrders.has(targetId)) {
      toggleExpand(targetId);
    }
    setPendingOrderId(null);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="dept-order-row-${targetId}"]`);
      scrollUnderPinnedChrome(el);
    }, 150);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `orders` joined the deps with the `target` lookup above — the effect now
    // asks a question about the unfiltered list, so it must re-run when that
    // list arrives.
  }, [pendingOrderId, loading, error, orders, filteredOrders, effectivePageSize, isAdmin]);

  /**
   * A column was picked — by the sort dropdown OR, since 2026-08-08, by a click
   * on a table header.
   *
   * 🔑 **ONE handler behind BOTH controls** (the sort dropdown went with the
   * order cards, 2026-10-18, leaving the headers as the only caller). This
   * function is unchanged by that work — it already had exactly the right shape, which is why
   * the headers were wired to it rather than given anything new. `Approvals.js`
   * has a byte-identical copy.
   *
   * **Third click keeps toggling; it does NOT return to the default sort.** Two
   * existing implementations behave this way (`GagBreakdown.js`'s `changeSort`
   * and this function as it already stood), and a "revert to default" third state
   * is invisible in the caret — the user cannot tell which of three states they
   * are in. Toggling forever is the settled convention.
   *
   * **Date and Total open DESCENDING, everything else ascending.** Newest and
   * biggest first is what a buyer wants, and it is what these two fields have
   * already done through the dropdown since before headers existed. Forcing a
   * blanket "first click ascending" would change a shipped behaviour nobody asked
   * about; of the two, only `order_total` has a header.
   *
   * ⚠️ Sorting deliberately does NOT reset the page — the `useEffect` above resets
   * `currentPage` on search / status / mode / production only. Pinned by
   * `DepartmentOrders.expandEdit.test.js` §6 ("changing the sort does not slice
   * the dirty row away"), which depends on the page NOT resetting.
   */
  /**
   * @param {string} field
   * @param {'asc'|'desc'} [explicitDir]  supplied ONLY by a cycling header.
   *
   * 🔀 **`explicitDir` added 2026-08-08 for the cycling Date/PO headers.** Those
   * columns advance through a fixed list of fields, each with ONE useful
   * direction, and never reverse — product owner: *"I dont think we need to ever
   * order anything in revers so just the 3 states I think?"*. So they set the
   * direction outright rather than going through the toggle.
   *
   * 🚨 **WITHOUT the argument this is byte-for-byte the function it was**, which
   * is what keeps every non-cycling header — and the whole Gag Breakdown, which
   * shares `ui/SortableHeader` — behaving exactly as before, direction toggle
   * included. `SortableHeader` only passes a second argument when a column
   * actually cycles.
   */
  // The rule itself is `utils/tableSort.js#applySortClick`, shared by all four tables.
  const handleSort = (field, explicitDir) => applySortClick(field, explicitDir, sortField, {
    setSortField, setSortDir, initialDir: orderInitialSortDir,
  });

  // Export handler - downloads the filtered orders as a CSV (opens in Excel)
  // 🔴 FIXED 2026-09-27: this opened with `setShowExportMenu(false)`, a setter
  // that no longer existed — the hand-rolled popover it closed became a
  // `ui/Dropdown` on 2026-08-10, whose item already calls `close()` — so the
  // first line threw a ReferenceError and neither export item did anything.
  // Pinned by `DepartmentOrders.export.test.js`.
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.set('format', 'csv');   // canonical; the server also still answers the old 'excel' token

      // Pass the exact filtered order IDs to ensure exported data matches the current view
      /* 🔀 `activeTab !== 'all'` replaced the status/completion terms on
         2026-09-12. The QUESTION is unchanged — "is the list on screen narrower
         than the department?" — and getting it wrong exports the whole
         department under the filename of one queue. */
      /* 🔻 The `filterMode !== 'all'` term went with the toggle (2026-09-12).
         The QUESTION is unchanged — "is the list on screen narrower than the
         department?" — and getting it wrong exports the whole department under
         the filename of one queue. */
      const hasFilters = activeTab !== 'all' || productionFilter !== 'all' || searchText;
      if (hasFilters && filteredOrders.length > 0) {
        params.set('ids', filteredOrders.map(o => o.id).join(','));
      } else if (hasFilters && filteredOrders.length === 0) {
        toastInfo('No orders to export with current filters.');
        setExporting(false);
        return;
      }

      const response = await apiGet('/api/orders/export?' + params.toString());

      // `apiThrow` keeps the server's reason (POL2 did the same for the row menu).
      if (!response.ok) await apiThrow(response);

      const blob = await response.blob();
      const filename = 'orders-export-' + new Date().toISOString().split('T')[0] + CSV_EXT;

      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [activeTab, productionFilter, searchText, filteredOrders, toastError, toastInfo]);

  // Rendering helpers — statusBadge and formatUrgency replaced by shared StatusBadge/UrgencyBadge components

  // formatDate imported from utils/dateFormat


  // Orders filtered by production (for counts in filter bar)
  const productionFilteredOrders = useMemo(() => {
    if (!isAdmin || productionFilter === 'all') return orders;
    return orders.filter(matchesProductionFilter);
  }, [orders, isAdmin, productionFilter, matchesProductionFilter]);

  // Count actionable orders (within production filter)
  /**
   * ── THE TAB COUNTS — THE DERIVATION IS THE CONTRACT (2026-09-12) ─────
   *
   * 🔑 **AFTER THE PRODUCTION FILTER, BEFORE PAGINATION — and before SEARCH
   * and before ACTIONABLE.** `productionFilteredOrders` is exactly that array,
   * and it is the same base `actionableCount` below already uses, so the two
   * numbers on this page's chrome can never be taken over different populations.
   *
   * Each half of that sentence is a decision:
   *   - **After the production filter**, because an admin narrowed to one show
   *     must not be told there are 40 orders to approve when 38 belong to a
   *     production they have filtered away.
   *   - **Before pagination**, because a count that describes the PAGE rather
   *     than the queue is worse than no count — it changes as you page through
   *     a list nobody edited.
   *   - **Before search and Actionable**, because the strip's job is to say what
   *     is waiting ELSEWHERE. A count narrowed by the current search would read
   *     `To order 0` for a buyer whose search box is full of something else,
   *     which is the opposite of the signal. 🔴 A PRODUCT decision, pinned as
   *     such by §2 of `DepartmentOrders.queueTabs.test.js`; changing it is the
   *     owner's call, not a tidy-up.
   *
   * 🔑 `all` is `productionFilteredOrders.length` — the rows themselves —
   * rather than a sum of the six queues. 🔁 Since the `Cancelled` tab landed
   * (2026-09-12 afternoon) the two AGREE for every recognised status, and that
   * identity is the whole reason the owner asked for the tab; §2 of
   * `DepartmentOrders.queueTabs.test.js` pins it. They are still computed
   * differently on purpose: a SUMMED `All` would silently omit any row whose
   * status `orderQueueTab` does not recognise, so counting the rows is what
   * keeps `All` meaning all if the identity is ever broken.
   *
   * 🫤 A count of `0` is a REAL answer and `ui/Tabs` renders it (`count != null`).
   */
  // 🔀 2026-09-30: the counting moved to `utils/tabCounts.js#countByTab`,
  // shared with My Orders' tabs — same rule, one copy.
  const tabCounts = useMemo(
    () => countByTab(productionFilteredOrders, ORDER_QUEUE_TAB_IDS, orderQueueTabs),
    [productionFilteredOrders]
  );

  const queueTabs = useMemo(
    () => ORDER_QUEUE_TABS.map((t) => ({ ...t, count: tabCounts[t.id] })),
    [tabCounts]
  );

  /* ONE tab handler for the strip's tap, its snap-to-centre settle (phone)
     and a swipe on the list — so every route asks first over a dirty edit. */
  const handleQueueTabChange = useCallback(
    (id) => continueWhenClean(() => { collapseAllOrders(); setActiveTab(id); }),
    [continueWhenClean, collapseAllOrders]
  );
  // A tap on the CURRENT tab also closes every opened order (owner 2026-10-03); a dirty edit asks first.
  const handleQueueTabReselect = useCallback(() => continueWhenClean(collapseAllOrders), [continueWhenClean, collapseAllOrders]);
  /* The queue tab strip, rendered in ONE of two wrappers (phone row /
     desktop bar) so its props cannot drift between them. */
  const queueTabStrip = (className) => (
    <Tabs
      scrollHints
      bare
      className={className}
      tabs={queueTabs}
      active={activeTab}
      // 🔀 2026-09-28 (owner: *"Trying to click on a tab … when we have
      // unsaved changes should cause the pop up to show"*): a dirty inline
      // edit ASKS first, then the tab changes. It used to change silently
      // with the dirty row pinned on screen; search and paging still pin.
      onChange={handleQueueTabChange}
      onReselect={handleQueueTabReselect}
      idPrefix="dept-orders-tab"
      ariaLabel="Order queues"
      /* 📱 Below `md` (2026-10-01): snap-to-centre + flush start beside
         the magnifier, exactly as My Orders — `ui/Tabs`' docblock. PHONE
         ONLY here: `snapCenter` also changes the strip's classes at desktop
         widths, and this page's desktop DOM is pinned unchanged. */
      snapCenter={isMobile}
      snapFlushStart={isMobile}
    />
  );
  /* 📱 SWIPE THE LIST TO CHANGE TAB (below `md`, 2026-10-01) — the My Orders
     rules (`utils/swipeTabs.js`) and wiring (`hooks/useSwipeTabs.js`). */
  const swipeAreaRef = useRef(null);
  const swipeHandlers = useSwipeTabs(swipeAreaRef, {
    tabs: queueTabs,
    active: activeTab,
    onChange: handleQueueTabChange,
    enabled: isMobile,
  });

  /* 🔻 `actionableCount` IS DELETED (2026-09-12). It had exactly two readers —
     the `· N actionable` clause in the header band and the `Actionable (N)`
     segment — and the owner removed BOTH in one ruling, so the memo is not
     merely unused, it is unreadable. ⚠️ `canActionOrder`, which it was derived
     from, STAYS and has five other readers including the green chevron on both
     row components. Checked before deleting, not assumed: `printFilterSummary`
     and the export's `hasFilters` never read this count. */

  if (loading) {
    return (
      <div className="max-w-full px-4 py-8">
        <LoadingSpinner message={currentRole === 'sub_department_lead' ? 'Loading sub department orders...' : 'Loading department orders...'} />
      </div>
    );
  }

  /**
   * 🔁 **`Actions ▾` — THE PAGE-LEVEL MENU, BUILT HERE AND RENDERED IN THE
   * STICKY TOOLBAR SINCE 2026-09-12.** Owner, on seeing the one-row bar on
   * staging (*"looks great"*): *"put the action menu on the same tab row but
   * keep it on the right."* It used to fill `ui/PageHeaderBand`'s `actions`
   * slot, in the band that SCROLLS AWAY; it is now the last control of the
   * toolbar's right slot, which is sticky — so it is reachable mid-scroll,
   * which is the whole reason the tabs moved onto that bar a few hours earlier.
   *
   * 🔑 **THE MENU, ITS ITEMS AND ITS PERMISSIONS ARE UNTOUCHED.** This is a
   * change of CONTAINER. Every `data-testid` below is unchanged, so the seven
   * suites that select on them keep working from the new location, and
   * `orderColumnResize.wiring.test.js` still reaches `Reset column widths`
   * through `dept-actions-menu`.
   *
   * 📐 **Z-INDEX: it lands in the `z-50` tier INSIDE the toolbar's `z-20`
   * stacking context — exactly where `Sort ▾` and `Filters ▾` already open
   * from on this same bar.** It needs no new tier and no `portal`: `ui/Dropdown`
   * defaults `portal` to `false`, `z-60` is the escape tier for panels that are
   * portalled to `document.body`, and the reason THIS one does not need it is
   * that `TOOLBAR_SHELL` declares no `overflow` and so clips nothing.
   * 🪤 `ui/CLAUDE.md` records that the one real "dropdown behind content" bug
   * in this repo was **CLIPPING by an ancestor's `overflow`, not z-index** —
   * check which before reaching for a bigger number.
   *
   * ⚠️ **It is a named const rather than JSX inline in the toolbar for a
   * mechanical reason: it is ~180 lines**, and inlining it would bury the bar's
   * four-control shape in the middle of them. It is declared here, immediately
   * before the `return`, because it reads handlers defined throughout the
   * component — hoisting it to sit beside `hasHeaderActions` would be a
   * use-before-define.
   */
  /**
   * 🔻 **`Refresh` WAS REMOVED 2026-08-08.** Product owner: *"lets loose
   * the 'Refresh' button on the department orders page seems a little
   * redundant. Everyone knows how to refresh a browser"*. It was the only
   * `Refresh` in the app — Approvals, My Orders and the Breakdown never
   * had one — so nothing else changes.
   *
   * 🚨 **AND THE SLOT BECAME A CONDITIONAL EXPRESSION IN THE SAME CHANGE,
   * WHICH IS NOT COSMETIC.** It used to be a fragment holding
   * `{isAdmin && <Export/>}` plus the always-present Refresh. With Refresh
   * gone, a NON-ADMIN would have passed `<>{false}</>` — a fragment that
   * renders nothing but **is still truthy**, and `PageHeaderBand` decides
   * with `Boolean(actions)`. That would have rendered an empty flex child
   * AND the status/actions hairline with nothing after it: a stray divider
   * floating to the right of the VAT badge for every non-admin.
   *
   * ⚠️ **THE HAIRLINE HALF OF THAT REASON EXPIRED ON 2026-08-08 WHEN THE
   * VAT BADGE WAS REMOVED, AND THE CONTRACT STILL STANDS — DO NOT RELAX
   * IT.** With `status` now absent the hairline needs both sides and can
   * never draw here, so an empty fragment would no longer produce a stray
   * divider. It would still produce the OTHER half: a truthy `actions`
   * renders the right-hand cluster as an empty flex child for every
   * non-admin. Passing `undefined` is what makes the omitted slot
   * genuinely absent, which is the contract `PageHeaderBand` documents and
   * its own tests pin.
   * ⚠️ Never hand this slot a fragment that can be conditionally empty.
   */
  /**
   * 🔀 **`Actions ▾` (2026-08-10) — THE HAND-ROLLED EXPORT DISCLOSURE IS
   * GONE AND ITS TWO ITEMS MOVED IN HERE.** Product owner: *"Thinking
   * about change filters on department orders to an action menu master
   * detials with a filter option. export all excell, and print all. also
   * maybe an action menu on the to approve page in a similar style?"* So
   * this is the first slice of something he is designing, not a chrome
   * change nobody asked for — and it matches the Breakdown's own
   * `Actions ▾`, which is what makes `Reset column widths` land in the
   * place a user has already learnt.
   *
   * ⛔ **"Print all" is NOT in scope** — he has not decided whether it
   * means one combined PDF, one per order, or a printable list. Do not
   * guess it in.
   *
   * 🔑 **It is now a `ui/Dropdown`, not a second hand-rolled popover.**
   * The old version re-implemented outside-click (a `mousedown` listener
   * plus a ref) and got no Escape handling and no focus return; the shared
   * primitive brings all three, plus `aria-expanded`/`aria-haspopup`. That
   * is the Code Unification Principle, not a refactor for its own sake.
   *
   * 🚨 **PERMISSIONS ARE UNCHANGED AND ARE WRITTEN BESIDE EACH ITEM.**
   * Export keeps its `isAdmin` gate and its `exporting` disabled state;
   * `Reset column widths` is gated on the LAYOUT, not on any authority.
   * This moved JSX — it granted nobody anything.
   *
   * ⚠️ **The slot stays a CONDITIONAL EXPRESSION, never a fragment that
   * can be conditionally empty.** `PageHeaderBand` decides with
   * `Boolean(actions)`, and a fragment rendering nothing is still truthy —
   * which draws an empty right-hand flex child for every non-admin.
   * `hasHeaderActions` is why this is safe.
   *
   * 🪤 **The menu renders only when it would hold at least one item.** An
   * empty disclosure is worse than no disclosure.
   */
  const pageActionsMenu = hasHeaderActions ? (
    <Dropdown
      align="right"
      haspopup="menu"
      panelClassName="w-64 p-1"
      // 📱 `max-md:px-2` only on the phone row (mobile batch); desktop markup byte-identical.
      triggerClassName={isMobile ? `${TOOLBAR_DISCLOSURE_TRIGGER} max-md:px-2` : TOOLBAR_DISCLOSURE_TRIGGER}
      title="Page actions"
      ariaLabel="Page actions"
      triggerTestId="dept-actions-menu"
      label="Actions"
    >
      {({ close }) => (
        <div role="menu" aria-label="Page actions">
          {/* 🔓 **THE `isAdmin` GATE ON EXPORT WAS DROPPED ON 2026-08-10, BY
              PRODUCT RULING, AND THIS IS A DELIBERATE WIDENING — say so if
              you touch it.** It read `{isAdmin && (…)}`.

              🔑 **The client gate now matches the SERVER gate for the same
              action**, which is CLAUDE.md's rule under *Role Checking*.
              `GET /api/orders/export` (`routes/orders.js:421`) has no admin
              check at all: it narrows in SQL through `visibleOrdersClause`
              and then post-filters every row through `canViewOrder` — so a
              non-admin can only ever export rows they can already see, and
              a buyer's export is bounded by their own departments. The
              genuinely unbounded case is an admin exporting everything, and
              admins already had that.

              🔑 **It also stops the page contradicting itself.** The
              per-row `Export CSV` in the expanded-row actions menu has
              been UNGATED since 2026-08-10, justified by the measurement
              that this endpoint emits none of `ACCOUNTING_ITEM_FIELDS`. The
              same bytes were already reachable one order at a time, so
              `isAdmin` here was friction rather than a boundary — and the
              route's own security comment says it outright: *"a hidden
              button is not access control."*

              ✅ **The accounting-leak check was RE-RUN against the handler,
              not taken on trust** (CLAUDE.md requires it): 31 columns on
              the populated path, and `cost_code` / `payment_type` /
              `budget_pot` appear nowhere. ⚠️ **Re-run it before adding any
              column to that export** — it now reaches every viewer of the
              page, not just admins. */}
          {(
            <>
              {/* The filtered-results hint from the old panel, kept: it is
                  the one thing that told the user Export follows the
                  active filters rather than dumping everything. */}
              {(searchText || activeTab !== 'all' || productionFilter !== 'all') && (
                <p className="px-3 pt-2 pb-1 text-xs text-gray-500 dark:text-gray-400">
                  Exporting filtered results ({filteredOrders.length} orders)
                </p>
              )}
              <button
                type="button"
                role="menuitem"
                onClick={() => { close(); handleExport(); }}
                disabled={exporting}
                title={`Download the currently-filtered orders as ${CSV_LABEL}`}
                className={ORDER_ACTIONS_MENU_ITEM}
                data-testid="dept-actions-export-csv"
              >
                {exporting ? 'Exporting…' : CSV_MENU_LABEL}
              </button>
            </>
          )}
          {/* 🔀 PRINT ALL (2026-08-10). Product owner: *"print all is a
              printable list, likey landscape to fit all columns on."*
              🚨 `filteredOrders`, NOT `paginatedOrders` — "all" means every
              row matching the filters, and the document states that in its
              own subtitle so a printed sheet cannot misrepresent its scope.
              🔴 UNGATED, like the per-row PDF rows beside it: the document
              is built client-side from rows already on screen.
              🔀 **`downloadTestId` ADDS A SECOND ROW, `Download list
              (PDF)` (2026-08-10)** — product owner: *"maybe even export pdf
              list. trying to stay consistant"*. One hook, one document
              builder, two rows; see the component. **To Approve gets the
              identical pair**, which is what the request was about. */}
          <OrderListPrintMenuItem
            orders={filteredOrders}
            headerTitle="Department Orders"
            departmentName={activeDepartment?.department_name || ''}
            filterSummary={printFilterSummary}
            // 🚨 FIXED 2026-09-28: this read `orders.length` (the UNFILTERED
            // department list) while `orders` above is `filteredOrders` (what
            // is actually rendered into the document) — so with any queue
            // tab, search term or production filter active,
            // `listPdf.js#reconciliationLine` printed a false "Note: the
            // caller reported N matching orders; M are listed here." on an
            // already-complete, correctly-scoped document. `totalCount` must
            // always be the count of the rows actually handed to the printer.
            totalCount={filteredOrders.length}
            filename="department-orders.pdf"
            onDone={close}
            testId="dept-actions-print-all"
            downloadTestId="dept-actions-download-list"
          />
          {/* 🔀 FIT COLUMNS TO SCREEN — REPLACES `Reset column widths`
              (owner ruling 2026-09-28, applying the Breakdown's own
              2026-09-27 ruling here: *"reset column widths should use the
              same code and changes as Fit columns to screen on the
              Breakdown page. same for other tables."*). The per-column
              reset is still a double-click on that column's own drag
              handle; this is the "put everything back" that does not
              require finding twelve handles — except now it fits the
              table to the screen (squeezing text columns, truncating where
              needed) rather than reverting to the measured defaults, and
              the FITTED widths are SAVED as the user's widths, in the same
              `sfx_order_table_col_widths` a drag writes.
              📝 The copy is `Fit columns to screen`, identical wording to
              the Breakdown's, deliberately.
              🔀 Stays at the BOTTOM of the menu, table-gated utility last
              — unchanged from `Reset column widths`'s own 2026-08-10
              placement rule. `orderColumnResize.wiring.test.js` §3 asserts
              both pages' item lists with `toEqual`. */}
          {canResetColumns && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { close(); fitColumnsToScreen(); }}
              disabled={!canFitColumns || columnsAlreadyFit}
              title={!canFitColumns
                ? 'Nothing measured to fit to yet'
                : columnsAlreadyFit
                  ? 'The columns already fit the screen'
                  : 'Fit every column to the available screen width'}
              className={ORDER_ACTIONS_MENU_ITEM}
              data-testid="dept-actions-fit-columns"
            >
              Fit columns to screen
            </button>
          )}
        </div>
      )}
    </Dropdown>
  ) : null;

  // The toolbar's right-hand controls (`Filters ▾` for admins + `Actions ▾`);
  // see the `rightSlot` comment on the Toolbar below for every rule they carry.
  const toolbarRightControls = (hasHeaderActions || (isAdmin && productionOptions.length > 0)) ? (
    <>
      {/* 🔻 THE `Sort ▾` DROPDOWN IS GONE (2026-10-01). It rendered only
          while `!isTableLayout` — cards, i.e. a phone — because cards
          have no headers (owner ruling 2026-09-12: on a table the
          clickable headers ARE the sort). A phone is a table now, so no
          width shows it. */}
      <OrderFilterBar
        /* 🔻 AND THE All / Actionable TOGGLE IS HIDDEN TOO (2026-09-12).
           `filterMode` / `onFilterModeChange` / `actionableCount` went with
           it — the component defaults all three and with the toggle hidden
           none is read. The reasoning is at the deleted `filterMode` state
           above.
           🚨 **FOR A NON-ADMIN THIS COMPONENT NOW RENDERS NOTHING AT ALL** —
           no toggle, no status group, and therefore no `Filters ▾`
           trigger — and it returns `null` rather than an empty
           `TOOLBAR_GROUP`. An ADMIN keeps the trigger, because the
           Production group survives. */
        hideViewToggle
        /* 🔻 THE STATUS GROUP IS HIDDEN ON THIS PAGE (2026-09-12). Owner:
           *"tabs replace the status checkboxes on this page"*. `statuses` /
           `selectedStatuses` / `onToggleStatus` / `onSelectAllStatuses` /
           `showCompleted` / `onShowCompletedChange` are all gone with it —
           the component defaults every one of them, and with the group
           hidden none is read. 🔻 `MyOrders.js`, which passed all of them,
           left this component for `ui/Tabs` on 2026-09-30; the group's
           byte-identity is still pinned by §9.1 of `OrderFilterBar.test.js`. */
        hideStatusGroup
        testIdPrefix="dept-filter"
        /* 🔀 The Production group (2026-08-10), moved off the page and into
           the panel. 🚨 THE ADMIN GATE IS HERE, ON THE PAGE, exactly where
           it was on the `<select>` this replaced — `OrderFilterBar` offers
           the group only when it receives a handler AND options, and
           decides nothing about who may see it. Passing `undefined` for a
           non-admin puts the panel into single-group mode, which is the
           shape `MyOrders` has. */
        productionOptions={isAdmin ? productionOptions : []}
        productionFilter={productionFilter}
        onProductionFilterChange={isAdmin ? setProductionFilter : undefined}
      />

      {/* 🔁 **LAST, AND THAT IS THE RULING, NOT A PREFERENCE.** Owner:
          *"put the action menu on the same tab row but keep it on the
          right."* Two items of one flex row are laid out in DOCUMENT
          ORDER, so being written after `Filters ▾` is the whole of what
          puts it on the outside. Built as `pageActionsMenu` above the
          `return` — read its docblock for the z-index answer and for why
          it left the header band. Pinned by §9.11 of
          `DepartmentOrders.queueTabs.test.js`. */}
      {pageActionsMenu}
    </>
  ) : null;

  return (
    <div className={`max-w-full p-2 pt-0 md:p-4 md:pt-0 lg:p-6 lg:pt-0 ${PAGE_SIDE_GUTTER}`} data-testid="department-orders-page">
      {leaveGuard.dialog}
      {/* ── HEADER BAND — context left, status + page actions right; scrolls away.
          The shared `ui/PageHeaderBand` shell; read its docblock for the
          two-band model and for why this band must NOT be sticky (the
          toolbar's `top-0 lg:top-14` is unchanged and needed no retuning). */}
      <PageHeaderBand
        testId="dept-header-band"
        hideContextOnPhone
        context={(
          <>
                {currentRole === 'sub_department_lead' ? 'Sub department orders' : 'All orders'} &middot; {filteredOrders.length} shown
                {/* 🔻 THE `· N actionable` CLAUSE IS GONE (2026-09-12), AND IT IS
                    THE SECOND CHANGE TO THIS SENTENCE IN TWO DAYS — WHICH IS THE
                    PART TO READ. It was `· N awaiting your action` until
                    2026-09-11, renamed because the owner objected that it was
                    literally false for a supervisor (`can_action` covers
                    `approved`/`ordered`/`partially_delivered` — orders to PLACE
                    and CHASE, nobody's approval step). **The rename fixed the
                    grammar and left the premise standing.** Pete, 2026-09-12:
                    *"Actionable is not necessarily my task — a supervisor with a
                    buyer does not want to see it for To order"*. The NUMBER was
                    the problem, so it is removed rather than renamed a third
                    time. 🔑 What replaces it is structure, not a label: the
                    queue tabs carry a per-queue count each, and the viewer's own
                    steps are on My Approvals and marked by the green chevron. */}
          </>
        )}
        /* 🔻 NO `status` SLOT SINCE 2026-08-08. The VAT badge was its only
           occupant on all three order pages, and the VAT display was removed
           from the UI by product decision — the reason is recorded in full in
           `hooks/useVatMode.js` and it is NOT "the badge was ugly": one boolean
           cannot express VAT rates that vary per line item, so the feature
           rendered confidently wrong figures for anything off the standard rate.
           ⚠️ Consequence for THIS page: with nothing in `status`, the
           status/actions hairline can no longer draw at all — it needs something
           on BOTH sides. That is why the page-level hairline assertions moved to
           asserting its ABSENCE; the component-level guarantee that it appears
           when both slots are filled still lives in `ui/PageHeaderBand.test.js`,
           which is where it belongs. */
        /* 🔁 **NO `actions` SLOT ON THIS PAGE SINCE 2026-09-12 — `Actions ▾`
           MOVED TO THE STICKY TOOLBAR.** Owner, on seeing the one-row bar on
           staging: *"put the action menu on the same tab row but keep it on the
           right."* The menu, its items and its permissions are unchanged; only
           its container is. It is built as `pageActionsMenu` just above this
           `return`, where the archaeology travelled with it.
           🔑 **Omitting the prop is the point, not a tidy-up.** `PageHeaderBand`
           decides with `Boolean(actions)`, so an omitted slot means the whole
           right-hand `ml-auto` cluster is never rendered — and with `status`
           gone too (the VAT badge, 2026-08-08) this band is now its LEFT box
           alone. Hand it `<>{false}</>` instead and you get an empty flex child
           back for every viewer; pinned by §9.12 of
           `DepartmentOrders.queueTabs.test.js`. */
      />

      {/* Success message */}
      {successMessage && (
        <div className="mb-4 px-4 py-3 bg-green-50 border border-green-200 text-green-800 dark:bg-green-900/30 dark:border-green-800 dark:text-green-300 rounded-lg text-sm font-medium" data-testid="dept-success-message">
          {successMessage}
        </div>
      )}

      {/* Arrow-wrapped: `fetchOrders` now takes a `silent` flag, and handing it
          straight to onRetry would pass the click event as that argument —
          truthy, so a retry would silently skip its own loading state. */}
      {error && <ErrorMessage error={error} onRetry={() => fetchOrders()} title="Failed to load orders" />}

      {/* 🔻 **THE ADMIN PRODUCTION `<select>` USED TO SIT HERE AND IS NOW A GROUP
          IN THE `Filters ▾` PANEL (2026-08-10).** It was a bare, unlabelled
          `<select>` in its own `mb-3` div ABOVE the toolbar — not on the filter
          bar, with no chip in `FilterChips` and no entry in
          `describeOrderFilters`. **So the one filter that can silently hide most
          of the page was the one filter the chip row never reported**, which is
          precisely the defect the chip row exists to prevent. Moving it in gives
          it a label, a chip, a rail tab with its own count, and the panel's
          `Clear filters` row.
          🟢 Nothing about WHO sees it changed: it is still admin-only, still
          `productionOptions.length > 0`, and `OrderFilterBar` is handed
          `onProductionFilterChange` only when both hold — the component never
          re-derives a permission. Its `data-testid="dept-production-filter"` is
          gone with the `<select>`; the radio rows carry
          `dept-filter-production-<id>`. */}

      {/* Toolbar — search, queue tabs, Filters ▾ (admins), Actions ▾.
          The sticky behaviour and the z-index contract (z-10 group headers <
          z-20 this bar < z-50 the Filters panel, which opens INSIDE this bar's
          stacking context) belong to `components/ui/Toolbar` — read its
          docblock before changing either.

          🚫 NO PAGE-SIZE SELECTOR HERE, and do not "restore" it. Product owner,
          2026-08-08: *"the top sticky bar is a mess, it seems to be spread over
          multiple rows etc. I would prefer as few rows as possible."* The bar
          carried a second, hand-rolled copy of the "Show 25 50 100 ALL" control
          that `components/Pagination.js:83-103` already renders at the bottom of
          this same page (`:1955` below) — the same four options writing the same
          `pageSize` state. It was a DUPLICATE, not a shortcut, and it emitted a
          duplicate `data-testid="page-size-<n>"` into the document alongside
          Pagination's, so `container.querySelector` in the client tests silently
          resolved whichever came first in the DOM. Both copies were `hidden
          md:flex` and `effectivePageSize` (`:822`) forces 50 below `md` anyway,
          so nothing is reachable now that was not reachable before.

          The old mobile-only "Filters" disclosure that used to wrap this row is
          gone: the Filters ▾ popover IS that disclosure, at every width, and
          keeping both would have been two toggles over one piece of state. One
          consequence is deliberate and worth knowing — sort field/direction are
          now visible on mobile instead of hidden behind that toggle. */}
      {/* ── ONE STICKY ROW (2026-09-12) ─────────────────────────────
          Owner: *"make sure the search box is on the tab line"*. The queue tabs
          ARE the toolbar's left group now, and the separate band above it is
          gone — one sticky row instead of two, which is the vertical height he
          asked to get back.
          🔻 **THIS SUPERSEDES `ui/CLAUDE.md`'s *"IT IS A THIRD BAND … NEVER ON
          THE TOOLBAR"*, FOR THIS PAGE ONLY**, and that sentence was mine. Its
          reasoning — *"the sticky band holds only what you reach for WHILE
          SCROLLING"* — still governs every other surface; what changed here is
          that the tabs ARE how you move around this page, so they are exactly
          what you reach for mid-scroll. The five-groups cap in
          `pages/CLAUDE.md` is likewise scoped: this bar carries tabs + search +
          `Filters ▾` (admins only).
          🔑 `bare` drops the strip's own band chrome — border and margin — so
          it sits in the toolbar rather than under it. It is NOT `className`:
          see the prop's docblock for the stylesheet-order reason.
          📱 `w-full lg:w-auto` is what makes the tabs take their OWN LINE below
          `lg` inside the flex-wrap shell, so the search box stays reachable
          rather than being squeezed off the end; `min-w-0` lets the tablist's
          own below-`sm` horizontal scroll actually engage.
          ⚠️ No `panelId`: the "panel" here is a paginated list plus a chip row
          plus an empty state, with no single element to name — and a dangling
          `aria-controls` announces a panel that does not exist. */}
      <Toolbar className={PHONE_HEADER_TOOLBAR}
        ref={toolbarRef}
        /* 🔁 **THE RIGHT SLOT IS `Filters ▾` AND, BELOW `md`, `Sort ▾` — THE
           SEARCH BOX MOVED OUT OF IT (2026-09-12, third ruling of the day).**
           Owner: *"the search box should be on the left, next to 'All' tab same
           row"*. So search leads the toolbar's LEFT group and the queue tabs
           follow it; `ml-auto` still pins whatever is left to the right edge.
           🔻 The Cards | Table toggle is GONE from this page (and from the
           other two since 2026-10-18); a per-page control is a SLOT THE PAGE
           FILLS rather than internal JSX.
           🚨 **`null`, NOT AN EMPTY BOX, WHEN NEITHER CAN RENDER.** A non-admin
           on a desktop hits both branches at once: there is no `Sort ▾` (the
           headers are the sort) and, with no Production group, `OrderFilterBar` renders
           nothing. An empty `ml-auto` flex item is not free — the tab strip
           beside it is `w-full` below `lg`, so it wraps onto a line of its own
           and costs `gap-y-2` of dead space under the tabs. Same rule and same
           reason as `OrderFilterBar`'s own `rendersNothing` guard and the one
           now inside `OrderSearchAndSort`; this is the page-level third of it.
           ⚠️ The condition mirrors what THIS page passes below — the Production
           group is the only group left once the status group is hidden — so if
           you ever pass `OrderFilterBar` another group, widen this with it.
           🔁 **AND SINCE 2026-09-12 `hasHeaderActions` IS IN THE CONDITION, WHICH
           MAKES THE `null` BRANCH UNREACHABLE — KEPT ANYWAY, AND THAT IS THE SAME
           CALL `hasHeaderActions` ITSELF MAKES.** `Actions ▾` is ungated
           (`Print all` is offered to every viewer in both layouts), so the slot
           now has an occupant for everyone. Gate `Print all`, or let the last
           ungated item leave that menu, and the empty box comes straight back —
           a live rule with its reason attached is cheaper than rediscovering it.
           Pinned by §9.8 of `DepartmentOrders.queueTabs.test.js`. */
        /* 📱 Below `md` the same controls ride the shared phone header's row
           2 (`actions`, 2026-10-03 — off the tab row). ONE expression,
           rendered in exactly one of the two places. */
        rightSlot={isMobile ? null : toolbarRightControls}
      >
        {/* 🔍 SEARCH FIRST, THEN THE TABS — that is the whole of the owner's
            ruling, and it is DOM ORDER that delivers it: two items of one
            flex-wrap row are laid out in document order, so nothing else decides
            which is on the left.
            📱 **Below `lg` the search keeps its OWN line, ABOVE the tabs.** The
            strip is `w-full` there, so it cannot share a line with anything, and
            the choice was between squeezing six scrollable tabs in beside an
            18rem box and giving the strip the whole line. The strip gets it. At
            `lg` and up they share one row, which is the width the ruling is
            about. Pinned by §9.2 and §9.7 of `DepartmentOrders.queueTabs.test.js`.
            🪤 The search box is `w-full sm:w-auto`, so below `sm` it takes a full
            line by its own sizing rather than by anything decided here. */}
        {isMobile ? (
          /* 📱 PHONE: the shared phone header
             (`components/MobileTabSearchRow.js`) — row 1 the queue tabs, row
             2 🔍 · Filters ▾ / Actions ▾ (2026-10-03). */
          <MobileTabSearchRow
            idPrefix="dept-orders"
            searchTestIdPrefix="dept"
            searchText={searchText}
            onSearchChange={setSearchText}
            searchPlaceholder="Search orders..."
            tabStrip={queueTabStrip('flex-1 min-w-0')}
            actions={toolbarRightControls}
          />
        ) : (
          <>
            <OrderSearchAndSort
              searchText={searchText}
              onSearchChange={setSearchText}
              searchPlaceholder="Search orders..."
              testIdPrefix="dept"
            />
            {queueTabStrip('w-full lg:w-auto min-w-0')}
          </>
        )}
      </Toolbar>

      {/* Active-filter chips. NOT optional garnish: a filter behind a disclosure
          would otherwise stop showing its own state, and a narrowed list looks
          exactly like a short one. Each ✕ RELAXES one filter. Search is not a
          chip — it has its own box above.
          🔀 The Production chip names the SELECTION, because that filter defaults
          to "all" — the mirror image of the status set. See
          `describeOrderFilters`. */}
      {/* 🔻 NO STATUS CHIPS AND NO "Completed hidden" CHIP ON THIS PAGE SINCE
          2026-09-12 — both described controls that are now the TAB STRIP, which
          is always visible above the toolbar and needs no chip to put its state
          back on the page. That was the chip row's whole justification (it
          mitigates a filter buried behind a disclosure), so a chip for a control
          nobody has to open would be a second control over one piece of state.
          🔑 **The PRODUCTION chip stays**, and it is why this row is still here:
          that filter IS behind the `Filters ▾` disclosure and is the one that
          can silently hide most of the page. Passing `statuses: []` /
          `selectedStatuses: []` yields no status chips by construction rather
          than by a new branch in the shared describer (`MyOrders.js` stopped
          reading it 2026-09-30). `showCompleted` is omitted and defaults `true` — no chip. */}
      {/* 🔻 AND NO "Actionable only" CHIP SINCE 2026-09-12 — the control it
          described is gone, so the branch is unreachable rather than merely
          unused. `filterMode` is omitted and `describeOrderFilters` defaults it
          to `'all'`; `setFilterMode` is no longer threaded into the patch
          appliers, so the PRODUCTION filter is the only thing this row can
          carry or clear on this page. */}
      <FilterChips
        chips={describeOrderFilters({
          statuses: [],
          selectedStatuses: [],
          productionFilter,
          productionOptions,
        })}
        onRemove={(chip) => applyOrderFilterPatch(chip.patch, { setProductionFilter })}
        onClearAll={() => applyOrderFilterPatch(
          { productionFilter: 'all' },
          { setProductionFilter }
        )}
      />

      {/* Orders list - unified expandable cards */}
      {/* 🪤 THE SEVENTH ROUTE, and the non-obvious member of the set — the one
          most likely to be missing in six months, because pinning the row into
          `paginatedOrders` LOOKS like it covers everything. It does not reach
          here: this branch is evaluated on the PRE-slice array, so a filter
          that empties the list swaps the whole table shell for an EmptyState
          and unmounts the editor regardless of what the slice contains.
          `&& paginatedOrders.length === 0` is a pure narrowing — `pageOfOrders`
          is a subset of `filteredOrders`, so the two conditions can only
          disagree when a dirty row has been pinned. */}
      {!error && filteredOrders.length === 0 && paginatedOrders.length === 0 ? (
        orders.length === 0 ? (
          <EmptyState
            title="No orders yet"
            description="Orders submitted by your team will appear here. Submit one yourself to get started."
            cta={{ label: 'Create an order', to: '/orders/new' }}
          />
        ) : (
          <EmptyState
            size="sm"
            title="No matching orders"
            description="Try adjusting your search or status filter."
          />
        )
      ) : (
        <>
          {/* 📱 ONE shell at every width since 2026-10-01 — the cards are
              gone. Below `md` it renders the PHONE column set and row
              (`activeTableColumns`, `OrderMobileTableRow`) with their own
              width model; at `md` and up the twelve, as before. `handleSort`
              is the SAME handler at both widths — the headers are the sort. */}
          <SwipeArea
            enabled={isMobile}
            areaRef={swipeAreaRef}
            handlers={swipeHandlers}
            testId="dept-orders-swipe-area"
          >
          <OrderTableShell
            testIdPrefix="dept"
            columns={activeTableColumns}
            sortField={sortField}
            sortDir={sortDir}
            onHeaderSort={handleSort}
            stickyTop={headerTop}
            columnWidths={activeColumnWidths}
            onColumnResize={activeSetColumnWidth}
            onColumnResetWidth={activeResetColumnWidth}
            onRegionWidthChange={setTableRegionWidth}
            // While one row holds unsaved edits every OTHER table row dims
            // (`opacity-40`) and stays CLICKABLE — the click asks the three-way
            // prompt (owner, 2026-09-28). THE rule is `ui/PageScrollTable`'s
            // `editingRowId`, shared with the hires Table; the card stack
            // ignores it. Approvals deliberately passes nothing (pages/CLAUDE.md).
            editingRowId={editDirtyId}
          >
            {paginatedOrders.map((order) => {
              const isExpanded = expandedOrders.has(order.id);
              const detail = orderDetails[order.id];
              const isLoadingDetail = detailsLoading[order.id];
              // 🔻 The card-only header nodes (cost-code/payment chips, the
              // Next-step line, the Poke/Override buttons) went with the cards
              // on 2026-10-01; the expanded row's own action surface carries
              // Poke and Approve on behalf (`hooks/useDeptOrderExpansion.js`).

              // 🔀 THE EXPANDED ROW MOVED, VERBATIM, TO `hooks/useDeptOrderExpansion.js`
              // (2026-09-27) so Department Hires can expand a hire into the same
              // panel. Every gate, handler and testid inside it is unchanged; read
              // the hook's `renderExpanded` for the per-row block that stood here.
              const expandedContent = renderExpanded(order);


              // The same `expandedContent` node goes to whichever row component
              // is active, so every role- and status-conditional action inside
              // it is defined once and the two views cannot disagree about what
              // a sub-department lead may do. Never re-derive a permission per
              // layout.
              /* 📱 The PHONE table swaps the ROW as well as the column set —
                 same testids, same `expandedContent`, same `actionable`
                 predicate (the green chevron rides in the PO cell). No
                 `onTitleClick` there: on a phone the whole row is the tap
                 target, and it expands. */
              if (isPhoneTable) {
                return (
                  <OrderMobileTableRow
                    key={order.id}
                    order={order}
                    isExpanded={isExpanded}
                    isLoadingDetail={isLoadingDetail}
                    onToggleExpand={toggleExpand}
                    detail={detail}
                    expandedContent={expandedContent}
                    actionable={canActionOrder(order)}
                    rowTestId={`dept-order-row-${order.id}`}
                    toggleTestId={`dept-order-toggle-${order.id}`}
                    detailsTestId={`dept-order-details-${order.id}`}
                    chevronTestId={`dept-chevron-${order.id}`}
                    testIdPrefix="dept"
                  />
                );
              }
              return (
                <OrderTableRow
                  key={order.id}
                  order={order}
                  isExpanded={isExpanded}
                  isLoadingDetail={isLoadingDetail}
                  onToggleExpand={toggleExpand}
                  detail={detail}
                  expandedContent={expandedContent}
                  // The green "yours to action" signal is the expand chevron,
                  // not an outline round the row (product owner, 2026-08-08 —
                  // see `constants/rowStyles.js`). The predicate is unchanged
                  // and stays HERE: OrderTableView must never re-derive it, and
                  // must never sniff it out of a class string either, which is
                  // why this is a boolean prop and not `rowClassName`.
                  actionable={canActionOrder(order)}
                  // 🔀 The dirty-edit dimming moved to the shell's
                  // `editingRowId` above (2026-09-28). It was
                  // `opacity-40 pointer-events-none` until the owner, on
                  // staging: *"Trying to click on a tab or another row when we
                  // have unsaved changes should cause the pop up to show"* —
                  // the click reaches `toggleExpand`, which asks first.
                  rowTestId={`dept-order-row-${order.id}`}
                  toggleTestId={`dept-order-toggle-${order.id}`}
                  detailsTestId={`dept-order-details-${order.id}`}
                  chevronTestId={`dept-chevron-${order.id}`}
                  testIdPrefix="dept"
                />
              );
            })}
          </OrderTableShell>
          </SwipeArea>

          {/* Pagination - Bottom. Since 2026-08-08 this is the ONLY page-size
              control on the page; the toolbar's duplicate copy is gone.

              🔑 PAGE SIZE IS A GLOBAL PREFERENCE (RULED 2026-09-02), the same
              family as `sfx_order_table_col_widths` / `sfx_dark_mode`: `getStoredPageSize` (`components/
              Pagination.js`) takes no parameter and always reads/writes the
              one shared `sfx_page_size` key. A page-scoped
              `sfx_dept_orders_page_size` used to be written here too, but
              nothing ever read it back — the dead write `npm run check-bloat`
              flagged as an orphan key — and it is deleted rather than fixed
              to be genuinely page-scoped, because honouring it would have
              changed the page size on load for every existing user of this
              page nobody had complained about. See root CLAUDE.md's
              `localStorage Keys` section. */}
          {filteredOrders.length > 0 && (
            <div className="mt-4">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                pageSize={effectivePageSize}
                onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
                totalItems={filteredOrders.length}
              />
            </div>
          )}
        </>
      )}

      {/* The expanded row's page-level dialogs — the unsaved-changes prompt,
          Order Completion, Skip remaining approvals and Approve on behalf —
          built by `hooks/useDeptOrderExpansion.js` and rendered here, at PAGE
          level, exactly where they always stood. */}
      {expansionDialogs}
    </div>
  );
}

export default DepartmentOrders;

/**
 * 📱 The swipe-to-change-tab area (below `md`, 2026-10-01): a plain block
 * carrying `useSwipeTabs`' handlers — on a PHONE only. At `md` and up it
 * renders its children bare, so the desktop DOM is byte-identical to the one
 * before the phone table (pinned by `DepartmentOrders.parityGolden.test.js`).
 */
function SwipeArea({ enabled, areaRef, handlers, testId, children }) {
  if (!enabled) return children;
  return <div ref={areaRef} {...handlers} data-testid={testId}>{children}</div>;
}
